/** stok — Stock counts (stok opname). Keys are flat, dot-namespaced and stable; placeholders use {name}. */
export const enStokCounts = {
  // ---- Statuses -------------------------------------------------------------------------
  'stok.countStatus.planned': 'Planned',
  'stok.countStatus.in_progress': 'Counting',
  'stok.countStatus.recount': 'Recount',
  'stok.countStatus.submitted': 'Awaiting approval',
  'stok.countStatus.approved': 'Approved',
  'stok.countStatus.rejected': 'Rejected',
  'stok.countStatus.posted': 'Posted',
  'stok.countStatus.cancelled': 'Cancelled',

  // ---- List -----------------------------------------------------------------------------
  'stok.counts.title': 'Stock counts',
  'stok.counts.subtitle': 'Physical counts of {branch}, compared with the ledger at a cutoff.',
  'stok.counts.plan': 'Plan a count',
  'stok.counts.planReadOnly':
    'You can view counts. Planning a count requires the stock adjustment permission.',
  'stok.counts.listTitle': 'Counts',
  'stok.counts.listDesc': 'Newest first. Progress and accuracy appear once you may see them.',
  'stok.counts.allStatuses': 'All statuses',
  'stok.counts.listSearch': 'Number or location',
  'stok.counts.empty': 'No stock counts yet — plan the first one.',
  'stok.counts.emptyReadOnly': 'No stock counts yet. Counts appear here once a manager plans one.',
  'stok.counts.emptyStatus': 'No counts with status “{status}”.',
  'stok.counts.colNumber': 'Number',
  'stok.counts.colProgress': 'Counted',
  'stok.counts.progressShort': '{counted} of {lines}',
  'stok.counts.accuracyOf': '{within} of {counted} lines',

  // ---- Facts ----------------------------------------------------------------------------
  'stok.counts.type': 'Count type',
  'stok.counts.typeFull': 'Full count',
  'stok.counts.typeCycle': 'Cycle count',
  'stok.counts.typeFullHint': 'Every active item at the location, and every lot with stock.',
  'stok.counts.typeCycleHint': 'Only the chosen items and/or one category.',
  'stok.counts.scope': 'Scope',
  'stok.counts.scopeCategory': 'Category {category}',
  'stok.counts.scopeItems': '{count} items',
  'stok.counts.blind': 'Blind count',
  'stok.counts.blindYes': 'Without system quantities',
  'stok.counts.blindNo': 'System quantities shown',
  'stok.counts.tolerance': 'Tolerance',
  'stok.counts.plannedDate': 'Planned date',
  'stok.counts.cutoff': 'Cutoff',
  'stok.counts.cutoffNotYet': 'Set when counting starts',
  'stok.counts.round': 'Count round',
  'stok.counts.notes': 'Notes',
  'stok.counts.createdBy': 'Planned by',
  'stok.counts.startedBy': 'Started by',
  'stok.counts.submittedBy': 'Submitted by',
  'stok.counts.decidedBy': 'Decided by',
  'stok.counts.decisionNote': 'Decision note',
  'stok.counts.postedBy': 'Posted by',
  'stok.counts.byAt': '{name}, {time}',
  'stok.counts.factsTitle': 'Count details',
  'stok.counts.lineWithLot': '{item} · lot {lot}',
  'stok.counts.lotLabel': 'Lot {lot}',

  // ---- Plan -----------------------------------------------------------------------------
  'stok.counts.backToList': 'Stock counts',
  'stok.counts.planTitle': 'Plan a stock count',
  'stok.counts.planSubtitle': 'One location of {branch}. Counting starts later, on the tablet.',
  'stok.counts.planNoPermission': 'Ask a manager to plan the count.',
  'stok.counts.noLocations': 'No active stock locations yet — create one first.',
  'stok.counts.planScopeTitle': 'Location and scope',
  'stok.counts.planScopeDesc': 'Choose where to count and which items.',
  'stok.counts.categoryHint':
    'Every item of this category is counted, in addition to the chosen items.',
  'stok.counts.noCategory': 'No category',
  'stok.counts.itemsLabel': 'Items',
  'stok.counts.itemsSelected': '({count} chosen)',
  'stok.counts.itemSearch': 'Search items',
  'stok.counts.clearItems': 'Clear selection',
  'stok.counts.noItemMatch': 'No items match “{query}”.',
  'stok.counts.planRulesTitle': 'Counting rules',
  'stok.counts.planRulesDesc': 'Lines outside the tolerance are counted again before approval.',
  'stok.counts.blindLabel': 'Count without system quantities (blind)',
  'stok.counts.blindHint':
    'Counters never see the system quantity or the difference; only approvers do. Recommended.',
  'stok.counts.tolerancePercent': 'Tolerance (%)',
  'stok.counts.toleranceHint': 'Allowed difference relative to the system quantity, e.g. 5 or 2,5.',
  'stok.counts.toleranceValue': 'Value tolerance ({currency})',
  'stok.counts.toleranceValueHint':
    'Also allowed when the difference is worth at most this amount.',
  'stok.counts.planNote': 'Stock only changes after the count is approved and posted.',
  'stok.counts.planSubmit': 'Plan count',
  'stok.counts.created': 'Count {number} planned.',
  'stok.counts.err.location': 'Choose a location.',
  'stok.counts.err.tolerance': 'Enter a percentage between 0 and 100 (at most two decimals).',
  'stok.counts.err.toleranceValue': 'Enter a whole amount.',
  'stok.counts.err.scope': 'Choose items or a category for a cycle count.',
  'stok.counts.err.tooManyItems': 'Choose at most {max} items.',

  // ---- Detail: planned / cancel ---------------------------------------------------------
  'stok.counts.detailTitle': 'Stock count {number}',
  'stok.counts.plannedTitle': 'Ready to count',
  'stok.counts.plannedDesc': 'Start when the counting team is at the location.',
  'stok.counts.start': 'Start counting',
  'stok.counts.starting': 'Starting…',
  'stok.counts.startReadOnly': 'Starting a count requires the stock count permission.',
  'stok.counts.startTitle': 'Start counting now?',
  'stok.counts.startBody':
    'Starting fixes the cutoff at this moment: the system quantity is every movement posted up to now. Service continues — sales and deliveries after the cutoff are taken into account when the count is submitted.',
  'stok.counts.startBlind': 'Counters will not see system quantities.',
  'stok.counts.started': 'Counting started. The cutoff is set.',
  'stok.counts.emptyScope': 'Nothing to count in this scope — no items or stock at this location.',
  'stok.counts.cancel': 'Cancel count',
  'stok.counts.cancelling': 'Cancelling…',
  'stok.counts.keepCount': 'Keep count',
  'stok.counts.cancelTitle': 'Cancel count {number}?',
  'stok.counts.cancelBody':
    'Entries already made are kept for the record, but stock is not changed.',
  'stok.counts.cancelled': 'Count cancelled.',
  'stok.counts.cancelledTitle': 'Count cancelled',
  'stok.counts.cancelledDesc': 'This count was stopped without changing stock.',
  'stok.counts.leftoverDrafts':
    '{count} unsaved entries remain on this device, but the count can no longer be edited.',
  'stok.counts.discardDrafts': 'Discard entries',

  // ---- Workspace ------------------------------------------------------------------------
  'stok.counts.workspaceTitle': 'Count',
  'stok.counts.workspaceRecountTitle': 'Recount',
  'stok.counts.cutoffNote': 'Cutoff {time}. Movements after it are accounted for on submission.',
  'stok.counts.blindNote':
    'Count what is physically there. System quantities and differences are shown to approvers only.',
  'stok.counts.expectedVisibleNote':
    'System quantities at the cutoff are shown because you may approve counts.',
  'stok.counts.draftNote': 'Entries are kept on this device until the server confirms them.',
  'stok.counts.countReadOnly':
    'You can view this count. Entering counts requires the stock count permission.',
  'stok.counts.recountBannerTitle': 'Recount — round {round}',
  'stok.counts.recountBannerBody':
    'Only the {count} marked lines can be changed. Count them again; the first count is kept for the approver.',
  'stok.counts.approverNote': 'Approver’s note: {note}',
  'stok.counts.searchLines': 'Find a line',
  'stok.counts.searchPlaceholder': 'Name, SKU or lot — Enter jumps to it',
  'stok.counts.filterLabel': 'Show lines',
  'stok.counts.filterAll': 'All',
  'stok.counts.filterTodo': 'To count ({count})',
  'stok.counts.filterUnsaved': 'Unsaved ({count})',
  'stok.counts.linesLabel': 'Count lines',
  'stok.counts.noLineMatch': 'No lines match “{query}”.',
  'stok.counts.noLinesInFilter': 'No lines in this view.',
  'stok.counts.qtyLabel': 'Counted quantity',
  'stok.counts.qtyInputLabel': 'Counted quantity: {item} ({unit})',
  'stok.counts.savedCount': 'Saved: {qty} · {name}',
  'stok.counts.previousCount': 'Previous count: {qty} · {name}',
  'stok.counts.firstCount': 'First count: {qty}',
  'stok.counts.expectedAtCutoff': 'System quantity at cutoff: {qty}',
  'stok.counts.lockedHint': 'Locked: only marked lines are counted again in this round.',
  'stok.counts.recountFlag': 'Recount',
  'stok.counts.state.locked': 'Locked',
  'stok.counts.state.conflict': 'Conflict',
  'stok.counts.state.invalid': 'Invalid number',
  'stok.counts.state.dirty': 'Not saved',
  'stok.counts.state.counted': 'Saved',
  'stok.counts.state.uncounted': 'Not counted',
  'stok.counts.uncountedLine': 'Not counted yet — enter 0 if none is found.',
  'stok.counts.invalidLine': 'Enter a quantity in {unit}, e.g. 2,5.',
  'stok.counts.conflictTitle': 'Changed on another device',
  'stok.counts.conflictServer': 'Server: {qty} · {name}',
  'stok.counts.conflictServerNone': 'Server: not counted',
  'stok.counts.conflictMine': 'Your entry: {qty}',
  'stok.counts.keepMine': 'Use my value',
  'stok.counts.useServer': 'Use server value',
  'stok.counts.conflictNotice':
    'Some lines were changed on another device ({count}). Choose which value to keep on each; your other entries are still unsaved — save again.',
  'stok.counts.uncountedNotice':
    '{count} lines are not counted yet. Count them (0 when none is found), save, then submit.',
  'stok.counts.invalidNotice': '{count} entries are not valid numbers and were not saved.',
  'stok.counts.refusedNotice':
    'The server refused {count} entries — see the marked lines. Nothing in that batch was saved.',
  'stok.counts.progress': 'Counted {done} of {total} · {remaining} left',
  'stok.counts.progressRecount': 'Recounted {done} of {total} · {remaining} left',
  'stok.counts.unsaved': 'Not saved: {count}',
  'stok.counts.allSaved': 'All entries saved',
  'stok.counts.save': 'Save',
  'stok.counts.saved': '{count} entries saved.',
  'stok.counts.submit': 'Submit count',
  'stok.counts.submitBlocked': 'Save every entry and resolve conflicts before submitting.',
  'stok.counts.submitTitle': 'Submit the count?',
  'stok.counts.submitBody':
    '{count} lines are compared with the ledger at the cutoff. Lines outside the tolerance are sent back for a recount; after that the count goes to an approver.',
  'stok.counts.submitBodyRecount':
    'The recount is compared with the ledger and goes to an approver, whatever the difference.',
  'stok.counts.submitRemaining': '{count} lines are not counted yet — the server will refuse.',
  'stok.counts.submitRemainingRecount':
    '{count} marked lines were not counted again — their first count is used.',
  'stok.counts.submitConfirm': 'Submit',
  'stok.counts.submitting': 'Submitting…',
  'stok.counts.submittedOk': 'Count submitted for approval.',
  'stok.counts.submittedRecount':
    'Some lines are outside the tolerance — count the marked lines again.',

  // ---- Review / decision ----------------------------------------------------------------
  'stok.counts.waitingTitle': 'Awaiting approval',
  'stok.counts.waitingDesc':
    'The count was submitted. Differences stay hidden from counters until an approver decides.',
  'stok.counts.statLines': 'Lines',
  'stok.counts.statLinesDesc': 'Items and lots on the count sheet.',
  'stok.counts.statCounted': 'Counted',
  'stok.counts.statCountedDesc': 'Lines with a count.',
  'stok.counts.statWithin': 'Within tolerance',
  'stok.counts.statWithinDesc': 'Counted lines inside the tolerance.',
  'stok.counts.statAccuracy': 'Accuracy',
  'stok.counts.statAccuracyDesc': 'Within tolerance ÷ counted lines.',
  'stok.counts.statVariance': 'Total difference',
  'stok.counts.statVarianceDesc': 'Value at average cost (+ gain, − loss).',
  'stok.counts.reviewTitle': 'Differences per line',
  'stok.counts.reviewDesc':
    'Difference = counted − (system quantity at cutoff + movements until the line was counted).',
  'stok.counts.outsideOnly': 'Outside tolerance only',
  'stok.counts.noOutside': 'Every line is within the tolerance.',
  'stok.counts.select': 'Select',
  'stok.counts.selectLine': 'Select {item}',
  'stok.counts.colExpected': 'System at cutoff',
  'stok.counts.colMovedAfter': 'Moved after cutoff',
  'stok.counts.colCounted': 'Counted',
  'stok.counts.colVarianceQty': 'Difference',
  'stok.counts.colVarianceValue': 'Difference value',
  'stok.counts.colTolerance': 'Tolerance',
  'stok.counts.within': 'Within tolerance',
  'stok.counts.outside': 'Outside tolerance',
  'stok.counts.recounted': 'Recounted',
  'stok.counts.noCost': 'No average cost',
  'stok.counts.sodTitle': 'Someone else must decide',
  'stok.counts.sodBody':
    'You took part in counting; another person must approve or reject (separation of duties). You can still request a recount.',
  'stok.counts.decideReadOnly': 'Approving or rejecting requires the stock approval permission.',
  'stok.counts.requestRecount': 'Request recount ({count})',
  'stok.counts.recountTitle': 'Request a recount?',
  'stok.counts.recountBody':
    '{count} chosen lines go back to the counters; the other lines are locked.',
  'stok.counts.recountNote': 'Note for the counters',
  'stok.counts.recountConfirm': 'Request recount',
  'stok.counts.recountRequested': 'Recount requested.',
  'stok.counts.approve': 'Approve',
  'stok.counts.approveTitle': 'Approve the count?',
  'stok.counts.approveBody':
    '{count} lines with a difference will become stock adjustments once the count is posted.',
  'stok.counts.decisionNoteLabel': 'Note',
  'stok.counts.selfDecisionNote': 'Reason (required — you counted this stock yourself)',
  'stok.counts.approved': 'Count approved.',
  'stok.counts.reject': 'Reject',
  'stok.counts.rejectTitle': 'Reject the count?',
  'stok.counts.rejectBody': 'Stock is not changed. Explain why, so the team can count again.',
  'stok.counts.rejectNote': 'Reason',
  'stok.counts.rejected': 'Count rejected.',
  'stok.counts.decisionTitle': 'Decision',
  'stok.counts.approvedDesc': 'Approved — post it to apply the differences to stock.',
  'stok.counts.rejectedDesc': 'Rejected — stock was not changed.',
  'stok.counts.postedSummary': 'Posted — the differences were applied to stock as count movements.',
  'stok.counts.post': 'Post adjustments',
  'stok.counts.posting': 'Posting…',
  'stok.counts.postReadOnly': 'Posting requires the stock adjustment or approval permission.',
  'stok.counts.postTitle': 'Post the count?',
  'stok.counts.postBody': '{count} adjustments will be posted to the stock ledger at average cost.',
  'stok.counts.postWarning': 'Posting changes stock and cannot be undone except by new movements.',
  'stok.counts.postedToast': '{count} adjustments posted.',
  'stok.counts.postedTitle': 'Adjustments posted',
  'stok.counts.postedDesc': 'Count movements written to the stock ledger.',
  'stok.counts.postedNone': 'No differences — nothing had to be posted.',
  'stok.counts.postedTotal': 'Total',
  'stok.counts.viewLedger': 'View movements',
} as const;
